<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import { GripVertical, Pencil } from 'lucide-vue-next'
import { User } from 'lucide-vue-next'
import BBtn from '@lib/components/base/BBtn.vue'
import QrPreview from '@lib/components/shared/QrPreview.vue'
import { useClipboardStore, type ClipboardCode } from '@/stores/clipboard'
import { buildTicketsetsCode, buildZonesCode, buildLoginCode, joinCommands } from '@/lib/configCode'
import { buildDeepLink, isDeepLinkable } from '@/lib/deepLink'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ code: ClipboardCode }>()
const emit = defineEmits<{
    (e: 'remove', id: number): void
    (e: 'split', id: number): void
    (e: 'merge-request', sourceId: number, targetId: number): void
    (e: 'update-print-label', id: number, label: string): void
}>()

const { t } = useI18n()
const clipboard = useClipboardStore()

const dragOver = ref<null | 'allow' | 'deny'>(null)

const qrCode = computed(() => {
    const loginPart = props.code.login ? buildLoginCode(props.code.login.username, props.code.login.password) : ''
    let raw: string
    if (props.code.kind === 'login') {
        raw = loginPart
    } else {
        const payload =
            props.code.kind === 'ticketsets'
                ? buildTicketsetsCode(props.code.items.map(i => i.id))
                : buildZonesCode(props.code.items.map(i => i.id))
        raw = joinCommands([loginPart, payload])
    }
    // When the "URL in QR" toggle is off, emit the bare payload (no deep-link).
    if (!clipboard.urlInQr) return raw
    return isDeepLinkable(props.code) ? buildDeepLink(raw) : raw
})

// LOGIN-only cards have nothing besides the username, which goes inline next
// to the user icon in the first row below the QR.
const label = computed(() => {
    if (props.code.kind === 'login') return ''
    return props.code.items.map(i => i.name).join(', ') || `#${props.code.id}`
})

const subtitle = computed(() => {
    if (props.code.kind === 'login') return ''
    return props.code.items.map(i => (i.eventName ? `${i.eventName} #${i.id}` : `#${i.id}`)).join(', ')
})

// LOGIN-only cards put the username in the first label row alongside the user
// icon; other cards prefix the row with the same icon when a login is attached.
const loginUsername = computed(() => props.code.login?.username ?? null)

// Custom print label: only ticketsets/zones cards can carry one. When set it
// replaces the item-name list on the printed sheet (the list stays visible in
// the clipboard UI). The row is an inline editable text, same pattern as the
// page-break title editor.
const canHavePrintLabel = computed(
    () => props.code.kind === 'ticketsets' || props.code.kind === 'zones'
)
const printLabel = computed(() => props.code.printLabel ?? '')

const editingLabel = ref(false)
const labelDraft = ref('')
const labelInputEl = ref<HTMLInputElement | null>(null)

async function startEditLabel() {
    labelDraft.value = printLabel.value
    editingLabel.value = true
    await nextTick()
    labelInputEl.value?.focus()
    labelInputEl.value?.select()
}

function saveLabel() {
    // Guard against double-emit: Enter triggers saveLabel(), then the input is
    // unmounted via v-if and its @blur fires saveLabel() again. The guard makes
    // the second call a no-op.
    if (!editingLabel.value) return
    editingLabel.value = false
    emit('update-print-label', props.code.id, labelDraft.value.trim())
}

function cancelEditLabel() {
    editingLabel.value = false
}

function onLabelKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
        e.preventDefault()
        saveLabel()
    } else if (e.key === 'Escape') {
        e.preventDefault()
        cancelEditLabel()
    }
}

const cardClasses = computed(() => {
    // Two distinct drag affordances live on this card:
    //   • the small handle (top-left) → Sortable reorder via pointer events
    //   • the rest of the card body   → native HTML5 drag for merge
    // Don't apply cursor-grab on the root or the whole card looks draggable in
    // both modes — confusing and breaks the Sortable handle contract.
    const base = 'border border-gray-200 rounded-12 p-3 flex items-stretch gap-3 overflow-hidden relative'
    if (props.code.kind === 'login') return `${base} bg-teal-50`
    const bodyTint = props.code.kind === 'ticketsets' ? 'bg-purple-50' : 'bg-sky-50'
    if (props.code.login) return `${base} ${bodyTint} border-l-[6px] border-l-teal-300`
    return `${base} ${bodyTint}`
})

// The split action is meaningful only when there's something to break apart —
// either more than one item, or a login attached to a non-login code.
const canSplit = computed(() => {
    if (props.code.kind === 'login') return false
    return props.code.items.length > 1 || props.code.login !== null
})

const dropOverlayClasses = computed(() => {
    if (dragOver.value === 'allow') return 'ring-2 ring-emerald-400 bg-emerald-50/60'
    if (dragOver.value === 'deny') return 'ring-2 ring-red-400 bg-red-50/40'
    return ''
})

// Native HTML5 DnD = merge. The card root is draggable (dragstart sets the
// source id), and the inner overlay accepts drops to fire merge-request.
// Sortable's pointer-based reorder is wired separately to a dedicated handle
// so the two systems don't fight over the same gesture.
function onDragStart(ev: DragEvent) {
    if (!ev.dataTransfer) return
    ev.dataTransfer.setData('application/x-clipboard-code', String(props.code.id))
    ev.dataTransfer.effectAllowed = 'move'
}

function isOurDrag(ev: DragEvent): boolean {
    return ev.dataTransfer?.types.includes('application/x-clipboard-code') ?? false
}

function onDragEnter(ev: DragEvent) {
    if (!isOurDrag(ev)) return
    ev.preventDefault()
    dragOver.value = 'allow'
}

function onDragOver(ev: DragEvent) {
    if (!isOurDrag(ev)) return
    ev.preventDefault() // accept the drop
}

function onDragLeave() {
    dragOver.value = null
}

function onDrop(ev: DragEvent) {
    if (!ev.dataTransfer) return
    const raw = ev.dataTransfer.getData('application/x-clipboard-code')
    dragOver.value = null
    if (!raw) return
    const sourceId = Number(raw)
    if (Number.isNaN(sourceId) || sourceId === props.code.id) return
    ev.preventDefault()
    ev.stopPropagation()
    emit('merge-request', sourceId, props.code.id)
}
</script>

<template>
    <div
        :class="cardClasses"
        :data-id="code.id"
        draggable="true"
        @dragstart="onDragStart"
    >
        <!--
            Reorder handle: Sortable is configured with `handle: '.clipboard-card-handle'`,
            so pointer drag only starts when the user grabs this icon. Native HTML5
            drag (= merge) still fires from anywhere on the card root.
        -->
        <div
            class="clipboard-card-handle absolute top-1 left-1 p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            :title="t('clipboard.dragHandle')"
        >
            <GripVertical class="w-4 h-4" />
        </div>
        <div class="flex-1 flex flex-col items-center gap-2 min-w-0 w-full">
            <!--
                QR canvas only — we render label/subtitle ourselves below so the
                first label row can contain a User icon next to the username
                when a login is attached.
            -->
            <QrPreview :code="qrCode" label="" :size="160" />
            <!-- First label row: optional login user-icon + name, then the label. -->
            <div class="text-sm font-semibold inline-flex items-center gap-1 text-center break-words">
                <User v-if="loginUsername" class="w-3.5 h-3.5 shrink-0 text-ink/70" />
                <span v-if="loginUsername" class="text-ink/70 font-medium">{{ loginUsername }}</span>
                <span v-if="loginUsername && label" class="text-gray-400">·</span>
                <span v-if="label">{{ label }}</span>
            </div>
            <!-- Second label row: subtitle (hidden in print). -->
            <div v-if="subtitle" class="text-xs text-gray-500 print:hidden text-center break-words">
                {{ subtitle }}
            </div>
            <!--
                Custom print label row (ticketsets/zones only, hidden in print
                because the print sheet renders printLabel itself). Inline edit,
                same pattern as the page-break title.
            -->
            <div v-if="canHavePrintLabel" class="w-full print:hidden">
                <input
                    v-if="editingLabel"
                    ref="labelInputEl"
                    v-model="labelDraft"
                    type="text"
                    class="w-full text-xs text-ink bg-white border border-teal rounded px-2 py-1 outline-none text-center"
                    :placeholder="t('clipboard.printLabel.placeholder')"
                    @keydown="onLabelKeydown"
                    @blur="saveLabel"
                />
                <button
                    v-else
                    type="button"
                    class="w-full inline-flex items-center justify-center gap-1 text-xs rounded px-2 py-1 hover:bg-black/5 transition-colors"
                    :class="printLabel ? 'text-ink font-medium' : 'text-gray-400 italic'"
                    :title="t('clipboard.printLabel.hint')"
                    @click="startEditLabel"
                >
                    <Pencil class="w-3 h-3 shrink-0" />
                    <span class="truncate">{{ printLabel || t('clipboard.printLabel.add') }}</span>
                </button>
            </div>
            <!-- Buttons pinned to the bottom so all cards in the grid align. -->
            <div class="mt-auto flex items-center gap-1 flex-wrap justify-center">
                <BBtn severity="secondary" size="small" @click="$emit('remove', code.id)">
                    {{ t('clipboard.remove') }}
                </BBtn>
                <BBtn
                    v-if="canSplit"
                    severity="secondary"
                    size="small"
                    @click="$emit('split', code.id)"
                >
                    {{ t('clipboard.split') }}
                </BBtn>
            </div>
        </div>
        <!--
            Merge drop-zone overlay: 60% of card area, transparent, accepts
            native HTML5 drops and lights up green while a card is being dragged
            over its centre.
        -->
        <div
            class="absolute pointer-events-auto rounded-12 transition-shadow"
            :class="dropOverlayClasses"
            :style="{ top: '20%', left: '20%', width: '60%', height: '60%' }"
            @dragenter="onDragEnter"
            @dragover="onDragOver"
            @dragleave="onDragLeave"
            @drop="onDrop"
        />
    </div>
</template>
